/**
 * @description Estimate calculation, formatting, and validation utilities
 *
 * Key features:
 * - Reuses shared calculation functions from invoiceHelpers where applicable
 * - Estimate-specific: status colors, display status, expiration helpers, default data
 * - No payment-related helpers (estimates don't track payments)
 *
 * @dependencies
 * - invoiceHelpers: shared calculateLineItem, calculateInvoiceTotals, formatDateForInput, etc.
 *
 * @notes
 * - Status model: draft, sent, accepted, declined, expired, converted
 * - Expiration: computed from status='sent' + expiration_date < today
 * - Re-exports LYN-243 line-item type resolvers from invoiceHelpers
 */

import {
  calculateLineItem,
  calculateInvoiceTotals,
  formatDateForInput,
  getTodayLocalDate,
  getDefaultLineItem,
  formatCurrency,
  getPaymentTermsDisplay,
  calculateDueDate,
  parseDateSafely,
  resolveGenericLineItemType,
  resolveCatalogLineItemType
} from './invoiceHelpers';

// Re-export shared helpers for convenience
export {
  calculateLineItem,
  formatDateForInput,
  getTodayLocalDate,
  getDefaultLineItem,
  formatCurrency,
  getPaymentTermsDisplay,
  calculateDueDate,
  resolveGenericLineItemType,
  resolveCatalogLineItemType
};

/**
 * Calculate estimate totals (reuses invoice calculation — same math)
 * @param {Array} items - Array of line items
 * @param {Object} estimateData - Estimate data with taxRate, discount, shipping
 * @returns {Object} Calculated totals
 */
export const calculateEstimateTotals = (items, estimateData = {}) => {
  return calculateInvoiceTotals(items, estimateData);
};

/**
 * Line items from GET estimate. Service nests them on estimate.items;
 * invoice-style sibling data.items is accepted when present and non-empty.
 *
 * @param {object} data - estimateService.getEstimate response
 * @returns {Array<object>}
 */
export function itemsFromEstimateResponse(data) {
  if (!data || data.success === false) return [];
  const sibling = data.items;
  const nested = data.estimate?.items;
  if (Array.isArray(sibling) && sibling.length > 0) return sibling;
  if (Array.isArray(nested)) return nested;
  return [];
}

/**
 * Get estimate status color classes
 * @param {string} status - Estimate status
 * @param {boolean} isExpired - Whether estimate is past expiration
 * @returns {string} Tailwind CSS classes
 */
export const getEstimateStatusColor = (status, isExpired = false) => {
  // Expired overrides sent status
  if (isExpired && status === 'sent') {
    return 'bg-orange-100 text-orange-700';
  }

  const statusColors = {
    'draft': 'bg-gray-100 text-gray-700',
    'sent': 'bg-blue-100 text-blue-700',
    'accepted': 'bg-green-100 text-green-700',
    'declined': 'bg-red-100 text-red-700',
    'expired': 'bg-orange-100 text-orange-700',
    'converted': 'bg-purple-100 text-purple-700'
  };

  return statusColors[status] || 'bg-gray-100 text-gray-700';
};

/**
 * Get estimate display status text
 * @param {string} status - Estimate status
 * @param {boolean} isExpired - Whether estimate is past expiration
 * @returns {string} Display text
 */
export const getEstimateDisplayStatus = (status, isExpired = false) => {
  if (isExpired && status === 'sent') return 'Expired';

  const statusMap = {
    'draft': 'Draft',
    'sent': 'Sent',
    'accepted': 'Accepted',
    'declined': 'Declined',
    'expired': 'Expired',
    'converted': 'Converted'
  };

  return statusMap[status] || status;
};

/**
 * Check if estimate is expired
 * @param {string} status - Current status
 * @param {string} expirationDate - Expiration date string
 * @returns {boolean} True if expired
 */
export const isEstimateExpired = (status, expirationDate) => {
  if (status !== 'sent' || !expirationDate) return false;
  const expDate = typeof expirationDate === 'string' ? parseDateSafely(expirationDate) : new Date(expirationDate);
  if (!expDate) return false;
  const today = getTodayLocalDate();
  return expDate < today;
};

/**
 * Check if estimate is editable based on status
 * @param {string} status - Current status
 * @returns {boolean} True if editable
 */
export const isEstimateEditable = (status) => {
  return status === 'draft';
};

/**
 * Check if estimate can be converted to invoice
 * @param {string} status - Current status
 * @returns {boolean} True if convertible
 */
export const canConvertToInvoice = (status) => {
  return status === 'accepted' || status === 'sent';
};

/**
 * Get available actions for an estimate based on its status
 * @param {string} status - Current status
 * @param {boolean} isExpired - Whether estimate is past expiration
 * @returns {Array} Array of available action names
 */
export const getAvailableActions = (status, isExpired = false) => {
  if (isExpired && status === 'sent') {
    return ['delete'];
  }

  const actionMap = {
    'draft': ['edit', 'send', 'delete'],
    'sent': ['accept', 'decline', 'convert', 'resend', 'delete'],
    'accepted': ['convert', 'delete'],
    'declined': ['delete'],
    'expired': ['delete'],
    'converted': ['viewInvoice']
  };

  return actionMap[status] || [];
};

/**
 * Validate estimate data before submission
 * @param {Object} estimateData - Estimate data to validate
 * @returns {Object} { isValid, errors }
 */
export const validateEstimate = (estimateData) => {
  const errors = {};

  if (!estimateData.customerId) {
    errors.customer = 'Customer is required';
  }

  if (!estimateData.issueDate && !estimateData.estimateDate) {
    errors.issueDate = 'Issue date is required';
  }

  // Validate items
  const items = estimateData.items || [];
  if (items.length === 0) {
    errors.items = 'At least one line item is required';
  } else {
    items.forEach((item, index) => {
      if (!item.itemName) {
        errors[`item_${index}_name`] = 'Item name is required';
      }
      if (!item.quantity || item.quantity <= 0) {
        errors[`item_${index}_quantity`] = 'Valid quantity is required';
      }
      if (item.unitPrice === undefined || item.unitPrice === null) {
        errors[`item_${index}_price`] = 'Unit price is required';
      }
    });
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Get default estimate data
 * @returns {Object} Default estimate structure
 */
export const getDefaultEstimateData = () => {
  const today = getTodayLocalDate();
  // Default expiration: 30 days from issue date
  const expirationDate = new Date(today);
  expirationDate.setDate(expirationDate.getDate() + 30);

  return {
    customerId: '',
    issueDate: formatDateForInput(today),
    expirationDate: formatDateForInput(expirationDate),
    paymentTerms: 'net_30',
    status: 'draft',
    items: [],
    taxRate: 0,
    discountType: null,
    discountValue: 0,
    discountCode: '',
    shippingCost: 0,
    internalNotes: '',
    customerNotes: '',
    terms: '',
    footer: ''
  };
};

/**
 * Calculate days until expiration
 * @param {string} expirationDate - Expiration date string
 * @returns {number|null} Days until expiration (negative if past), null if no date
 */
export const daysUntilExpiration = (expirationDate) => {
  if (!expirationDate) return null;
  const expDate = typeof expirationDate === 'string' ? parseDateSafely(expirationDate) : new Date(expirationDate);
  if (!expDate) return null;
  const today = getTodayLocalDate();
  const diffMs = expDate - today;
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
};
